import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChainBadge } from "@/components/ChainBadge";
import { api, type AgentObject, type AuditEvent } from "@/lib/api";
import { suiExplorer, actionLabel, dedupAudit, auditActionLabel } from "@/lib/sui";
import { PREVIEW, previewFleetAudit } from "@/lib/suiPreview";

/**
 * Chronicle — operator-wide Sui audit feed. The closing trust shot of the demo:
 * every agent action, allowed or denied, in one ruled ledger. Pulls the
 * fleet-wide /audit/search and scopes to the operator's Sui agents.
 *
 * DESIGN.md (Persian + Orange): ghost ground, indigo structure, coral denial,
 * mono data, serif-italic title, full borders, reduced-motion safe.
 */

type Filter = "all" | "allowed" | "denied";

function short(id: string | null, head = 8, tail = 6): string {
  if (!id) return "—";
  return id.length <= head + tail + 1 ? id : `${id.slice(0, head)}…${id.slice(-tail)}`;
}

export function SuiChroniclePage({ agents, token }: { agents: AgentObject[]; token: string }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [events, setEvents] = useState<(AuditEvent & { agent_id: string | null })[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const loadedRef = useRef(false);
  // Liveness: poll the ledger every 3s so agent actions surface within ~3-5s.
  useEffect(() => {
    if (PREVIEW) return;
    const t = setInterval(() => setReloadKey((k) => k + 1), 3000);
    return () => clearInterval(t);
  }, []);

  const names = useMemo(() => {
    const m = new Map<string, string>();
    agents.forEach((a) => m.set(a.id, a.name));
    return m;
  }, [agents]);

  useEffect(() => {
    if (PREVIEW) { setEvents(previewFleetAudit(filter).filter((e) => e.agent_id && names.has(e.agent_id))); setLoading(false); setErr(null); return; }
    if (!token) return;
    let cancelled = false;
    if (!loadedRef.current) setLoading(true); // skeleton only on first load, not on polls
    setErr(null);
    api
      .searchAudit(token, { filter, limit: 100 })
      .then((r) => { if (!cancelled) { const next = r.events.filter((e) => e.agent_id && names.has(e.agent_id)); setEvents((prev) => prev.length === next.length && prev.every((e, i) => e.id === next[i]?.id) ? prev : next); loadedRef.current = true; } })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : "audit unavailable"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, filter, names, reloadKey]);

  const evs = dedupAudit(events);
  const counts = {
    all: evs.length,
    allowed: evs.filter((e) => e.decision === "allowed").length,
    denied: evs.filter((e) => e.decision === "denied").length,
  };

  return (
    <div className="ch">
      <header className="ch__head">
        <div className="ch__kick">sui · audit ledger</div>
        <h1 className="ch__title">Chronicle</h1>
        <p className="ch__sub">Every action your agents take, allowed or denied, settled on-chain and recorded here.</p>
      </header>

      <div className="ch__bar">
        <div className="ch__stats">
          <span><b>{counts.all}</b> events</span>
          <span className="ch__ok"><b>{counts.allowed}</b> allowed</span>
          <span className="ch__deny"><b>{counts.denied}</b> denied</span>
        </div>
        <div className="ch__tabs">
          {(["all", "allowed", "denied"] as Filter[]).map((f) => (
            <button key={f} className={filter === f ? "on" : ""} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="ch__skel">{[0, 1, 2, 3].map((i) => <div key={i} className="ch__skel-row" />)}</div>
      ) : err || evs.length === 0 ? (
        <div className="ch__empty">
          {err ? "Audit index unavailable right now." : "No events yet. Once an agent acts on-chain, the ledger fills here."}
        </div>
      ) : (
        <table className="ch__tbl">
          <thead><tr><th>Time</th><th>Agent</th><th>Action</th><th className="r">Amount</th><th>Tx / reason</th><th className="r">Result</th></tr></thead>
          <tbody>
            {evs.map((e) => {
              const denied = e.decision === "denied";
              const d = e.action_descriptor ?? {};
              const when = e.block_time ?? e.created_at;
              return (
                <tr key={e.id} className={denied ? "is-deny" : ""}>
                  <td className="t">{new Date(when).toLocaleString()}</td>
                  <td>
                    {e.agent_id ? <Link to={`/sui/agents/${e.agent_id}`} className="ch__agent"><ChainBadge chain="sui" /> {names.get(e.agent_id) ?? short(e.agent_id)}</Link> : "—"}
                  </td>
                  <td>{auditActionLabel(e)}</td>
                  <td className="r">{d.amount != null ? `${d.amount} ${d.asset ?? ""}`.trim() : "—"}</td>
                  <td>{e.tx_signature
                    ? <a href={suiExplorer.tx(e.tx_signature)} target="_blank" rel="noopener noreferrer">{short(e.tx_signature)} ↗</a>
                    : <span className="ch__reason">{e.reason_detail ?? (e.reason_code ? actionLabel(e.reason_code) : "—")}</span>}</td>
                  <td className="r"><span className={`res ${denied ? "deny" : "ok"}`}>{denied ? "— denied" : "✓ allowed"}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <style>{`
        .ch { max-width: var(--page-max); margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
        .ch__head { margin-bottom: var(--s-5); }
        .ch__kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent-deep); }
        .ch__title { font: var(--type-display-page); font-style: italic; margin: var(--s-2) 0 var(--s-2); }
        .ch__sub { font: var(--type-body-large); color: var(--slate); max-width: 56ch; line-height: 1.55; margin: 0; }
        .ch__bar { display: flex; align-items: center; justify-content: space-between; gap: var(--s-5); margin-bottom: var(--s-4); padding-bottom: var(--s-4); border-bottom: 1px solid var(--line); }
        .ch__stats { display: flex; gap: var(--s-5); font: var(--type-mono-data); color: var(--slate); }
        .ch__stats b { color: var(--bone); } .ch__ok b { color: var(--ok); } .ch__deny b { color: var(--oxblood); }
        .ch__tabs { display: flex; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; }
        .ch__tabs button { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; padding: 7px 14px; border: 0; border-right: 1px solid var(--line); background: var(--bg); color: var(--slate); }
        .ch__tabs button:last-child { border-right: 0; } .ch__tabs button.on { background: var(--bone); color: var(--text-on-ink); }
        .ch__tbl { width: 100%; border-collapse: collapse; }
        .ch__tbl th { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-3); text-align: left; font-weight: 400; padding: 0 0 10px; }
        .ch__tbl th.r, .ch__tbl td.r { text-align: right; }
        .ch__tbl td { font: var(--type-mono-data); padding: 11px 0; border-top: 1px solid var(--line-soft); vertical-align: middle; }
        .ch__tbl th:not(:last-child), .ch__tbl td:not(:last-child) { padding-right: var(--s-6); }
        .ch__tbl td.t { color: var(--slate-2); }
        .ch__agent { display: inline-flex; align-items: center; gap: 7px; color: var(--bone); }
        .ch__agent:hover { color: var(--accent-deep); }
        .ch__tbl a { color: var(--slate); } .ch__tbl a:hover { color: var(--accent-deep); }
        .ch__reason { color: var(--oxblood); font: var(--type-mono-address); }
        .ch__tbl tr.is-deny td { background: var(--oxblood-tint); }
        .ch__tbl tbody tr { animation: ch-row-in 300ms cubic-bezier(0.23,1,0.32,1); }
        @keyframes ch-row-in { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .ch__tbl tbody tr { animation: none; } }
        .res { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; } .res.ok { color: var(--ok); } .res.deny { color: var(--oxblood); }
        .ch__empty { padding: var(--s-8) 0; text-align: center; font: var(--type-body); color: var(--slate-2); }
        .ch__skel { display: flex; flex-direction: column; gap: 8px; margin-top: var(--s-4); }
        .ch__skel-row { height: 32px; background: linear-gradient(90deg, var(--line-soft), var(--bg), var(--line-soft)); background-size: 200% 100%; animation: ch-shim 1.2s ease-in-out infinite; border-radius: 4px; }
        @keyframes ch-shim { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @media (prefers-reduced-motion: reduce) { .ch__skel-row { animation: none; } }
      `}</style>
    </div>
  );
}
